/**
 * Authenticated file download.
 *
 * The API may either:
 *  - 302-redirect to a pre-signed CDN URL (R2 mode), or
 *  - stream the file body directly (lean disk mode).
 *
 * Either way we want the same UX: a real "Save As / download" instead of
 * navigating the current tab away. We blob the response and trigger a
 * hidden anchor click with the desired filename.
 */
export async function downloadAuthed(opts: {
  url: string;
  filename: string;
  accessToken: string | null | undefined;
  tenantId?: string | null;
}): Promise<void> {
  const headers: Record<string, string> = {};
  if (opts.accessToken) headers.Authorization = `Bearer ${opts.accessToken}`;
  if (opts.tenantId) headers['X-Tenant-Id'] = opts.tenantId;

  const res = await fetch(opts.url, {
    method: 'GET',
    credentials: 'include',
    headers,
    // Default redirect handling — the browser follows 302s and gives us the bytes.
  });
  if (!res.ok) {
    throw new Error(`Download failed (HTTP ${res.status})`);
  }
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = opts.filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoke after a tick so Safari has a chance to attach the download.
  setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
}
